import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authApi } from '@/lib/pillarApi';
import { AnchorbaseLogo } from '@/components/branding/AnchorbaseLogo';

function isPasswordRequiredActivationError(error: unknown): boolean {
    return (
        error instanceof Error &&
        error.message.includes('Password is required to activate your account')
    );
}

export function VerifyEmailContent() {
    const params = useParams();
    const navigate = useNavigate();
    const token = params.token;
    if (token === undefined) {
        throw new Error('Verification token is required');
    }
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [requiresPassword, setRequiresPassword] = useState(false);
    const verifyMutation = useMutation({
        mutationFn: () => authApi.verifyEmail(token, password),
        onSuccess: () => {
            // Success - user can click button to go to login
        },
        onError: (error) => {
            if (isPasswordRequiredActivationError(error)) {
                setRequiresPassword(true);
            }
        },
    });

    useEffect(() => {
        if (!requiresPassword) {
            verifyMutation.mutate();
        }
    }, [requiresPassword]);

    const validatePasswords = (): boolean => {
        setPasswordError('');

        if (!password) {
            setPasswordError('Password is required');
            return false;
        }

        if (password.length < 8) {
            setPasswordError('Password must be at least 8 characters long');
            return false;
        }

        if (password !== confirmPassword) {
            setPasswordError('Passwords do not match');
            return false;
        }

        return true;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!validatePasswords()) {
            return;
        }

        verifyMutation.mutate();
    };

    const isEmailVerificationCompletion: boolean =
        verifyMutation.isSuccess &&
        (verifyMutation.data.message.toLowerCase().includes('secondary email address') ||
            verifyMutation.data.message.toLowerCase().includes('already verified'));
    const verificationPageTitle: string = isEmailVerificationCompletion
        ? 'Email Verified'
        : 'Account Activation';

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <Card className="max-w-md w-full">
                <CardHeader>
                    <CardTitle className="text-center">{verificationPageTitle}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {verifyMutation.isSuccess && (
                        <>
                            <Alert>
                                <AlertDescription className="text-center">
                                    <div className="text-green-600 font-semibold text-lg mb-2">
                                        <div className="flex justify-center mb-2">
                                            <AnchorbaseLogo width={220} />
                                        </div>
                                    </div>
                                    <p className="mb-2">{verifyMutation.data.message}</p>
                                    <p className="text-sm">
                                        Account:{' '}
                                        <span className="font-mono">
                                            {verifyMutation.data.user.primary_email}
                                        </span>
                                    </p>
                                </AlertDescription>
                            </Alert>

                            <div className="space-y-4">
                                <Button onClick={() => navigate('/login')} className="w-full">
                                    Go to Login
                                </Button>
                            </div>
                        </>
                    )}

                    {!verifyMutation.isSuccess && requiresPassword && (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="password">Password</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    disabled={verifyMutation.isPending}
                                    required
                                    minLength={8}
                                />
                                <p className="text-xs text-muted-foreground">
                                    Password must be at least 8 characters long
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirm Password</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm your password"
                                    disabled={verifyMutation.isPending}
                                    required
                                />
                            </div>

                            {passwordError && (
                                <Alert variant="destructive">
                                    <AlertDescription>{passwordError}</AlertDescription>
                                </Alert>
                            )}

                            {verifyMutation.isError &&
                                !isPasswordRequiredActivationError(verifyMutation.error) && (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        <div className="font-semibold mb-2">Activation Failed</div>
                                        <p className="text-sm">
                                            {verifyMutation.error instanceof Error
                                                ? verifyMutation.error.message
                                                : 'Failed to activate account. The token may be invalid, expired, or already used.'}
                                        </p>
                                    </AlertDescription>
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                className="w-full"
                                disabled={verifyMutation.isPending}
                            >
                                {verifyMutation.isPending ? 'Activating...' : 'Activate Account'}
                            </Button>

                            {verifyMutation.isPending && (
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                </div>
                            )}
                        </form>
                    )}
                    {!verifyMutation.isSuccess &&
                        !requiresPassword &&
                        verifyMutation.isPending && (
                            <div className="text-center text-sm text-muted-foreground">
                                Verifying token...
                            </div>
                        )}
                    {!verifyMutation.isSuccess &&
                        !requiresPassword &&
                        verifyMutation.isError && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {isPasswordRequiredActivationError(verifyMutation.error)
                                        ? 'Please set your password to complete account activation.'
                                        : verifyMutation.error instanceof Error
                                        ? verifyMutation.error.message
                                        : 'Failed to verify token'}
                                </AlertDescription>
                            </Alert>
                        )}
                </CardContent>
            </Card>
        </div>
    );
}
